// SimpleRichTextEditor.jsx - Clean, reliable version
import React, { useState, useRef, useEffect, useCallback } from 'react';

const SimpleRichTextEditor = ({ value, onChange, readOnly = false, height = '400px', reservedWords = [] }) => {
    const [showVariables, setShowVariables] = useState(false);
    const [searchVariable, setSearchVariable] = useState('');
    const editorRef = useRef(null);
    const lastValueRef = useRef(value || '');

    // Initialize editor content
    useEffect(() => {
        if (editorRef.current && value !== undefined) {
            const content = value || '';
            editorRef.current.innerHTML = content;
            lastValueRef.current = content;
        }
    }, [value]);

    // Handle editor changes
    const handleEditorChange = useCallback(() => {
        if (!editorRef.current) return;

        const content = editorRef.current.innerHTML;

        // Only call onChange if content actually changed
        if (content !== lastValueRef.current) {
            lastValueRef.current = content;
            onChange?.(content);
        }
    }, [onChange]);

    // Insert formatting
    const formatText = useCallback((command, value = null) => {
        if (readOnly || !editorRef.current) return;

        // Focus editor
        editorRef.current.focus();

        // Execute command
        document.execCommand(command, false, value);

        // Trigger change
        handleEditorChange();
    }, [readOnly, handleEditorChange]);

    // Insert variable
    const insertVariable = useCallback((variable) => {
        if (readOnly || !editorRef.current) return;

        const editor = editorRef.current;
        editor.focus();

        const selection = window.getSelection();

        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const variableSpan = document.createElement('span');
            variableSpan.className = 'template-variable';
            variableSpan.textContent = `{{${variable}}}`;
            variableSpan.contentEditable = 'false';
            variableSpan.style.userSelect = 'none';
            variableSpan.style.display = 'inline-block';
            variableSpan.style.fontFamily = 'monospace';
            variableSpan.style.background = '#e3f2fd';
            variableSpan.style.padding = '2px 6px';
            variableSpan.style.borderRadius = '3px';
            variableSpan.style.color = '#11315A';
            variableSpan.style.border = '1px solid #90caf9';

            range.deleteContents();
            range.insertNode(variableSpan);

            // Move cursor after variable
            const newRange = document.createRange();
            newRange.setStartAfter(variableSpan);
            newRange.setEndAfter(variableSpan);
            selection.removeAllRanges();
            selection.addRange(newRange);
        } else {
            // Append at end
            const variableSpan = document.createElement('span');
            variableSpan.className = 'template-variable';
            variableSpan.textContent = `{{${variable}}}`;
            variableSpan.contentEditable = 'false';
            variableSpan.style.userSelect = 'none';
            variableSpan.style.display = 'inline-block';
            variableSpan.style.fontFamily = 'monospace';
            variableSpan.style.background = '#e3f2fd';
            variableSpan.style.padding = '2px 6px';
            variableSpan.style.borderRadius = '3px';
            variableSpan.style.color = '#11315A';
            variableSpan.style.border = '1px solid #90caf9';

            editor.appendChild(variableSpan);

            // Move cursor to end
            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }

        handleEditorChange();
    }, [readOnly, handleEditorChange]);

    // Handle paste - clean text only
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
    }, []);

    // Handle copy for variables
    const copyToClipboard = async (text, buttonElement) => {
        try {
            await navigator.clipboard.writeText(text);
            const originalText = buttonElement.textContent;
            buttonElement.textContent = '✓';
            buttonElement.style.background = '#10b981';

            setTimeout(() => {
                buttonElement.textContent = originalText;
                buttonElement.style.background = '';
            }, 1500);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const filteredVariables = reservedWords.filter(word =>
        (word.id && word.id.toLowerCase().includes(searchVariable.toLowerCase())) ||
        (word.name && word.name.toLowerCase().includes(searchVariable.toLowerCase()))
    );

    return (
        <div className="simple-rich-text-editor" dir="ltr">
            {!readOnly && (
                <div className="editor-toolbar">
                    <div className="toolbar-left">
                        <button
                            type="button"
                            onClick={() => setShowVariables(!showVariables)}
                            className={`variables-toggle ${showVariables ? 'active' : ''}`}
                            title="Toggle variables panel"
                        >
                            <span className="icon">📋</span>
                            Variables
                        </button>
                    </div>

                    <div className="toolbar-right">
                        <div className="format-buttons">
                            <button
                                type="button"
                                onClick={() => formatText('bold')}
                                title="Bold (Ctrl+B)"
                                className="format-btn"
                            >
                                <strong>B</strong>
                            </button>
                            <button
                                type="button"
                                onClick={() => formatText('italic')}
                                title="Italic (Ctrl+I)"
                                className="format-btn"
                            >
                                <em>I</em>
                            </button>
                            <button
                                type="button"
                                onClick={() => formatText('underline')}
                                title="Underline (Ctrl+U)"
                                className="format-btn"
                            >
                                <u>U</u>
                            </button>

                            <div className="separator"></div>

                            <button
                                type="button"
                                onClick={() => formatText('insertUnorderedList')}
                                title="Bullet List"
                                className="format-btn"
                            >
                                •
                            </button>
                            <button
                                type="button"
                                onClick={() => formatText('insertOrderedList')}
                                title="Numbered List"
                                className="format-btn"
                            >
                                1.
                            </button>

                            <div className="separator"></div>

                            <button
                                type="button"
                                onClick={() => formatText('justifyLeft')}
                                title="Align Left"
                                className="format-btn"
                            >
                                ⎸
                            </button>
                            <button
                                type="button"
                                onClick={() => formatText('justifyCenter')}
                                title="Align Center"
                                className="format-btn"
                            >
                                ⎹
                            </button>
                            <button
                                type="button"
                                onClick={() => formatText('justifyRight')}
                                title="Align Right"
                                className="format-btn"
                            >
                                ⎸
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!readOnly && showVariables && (
                <div className="variables-panel">
                    <div className="variables-search">
                        <input
                            type="text"
                            placeholder="Search variables..."
                            value={searchVariable}
                            onChange={(e) => setSearchVariable(e.target.value)}
                            className="search-input"
                            dir="ltr"
                        />
                    </div>

                    <div className="variables-list">
                        {filteredVariables.length > 0 ? (
                            filteredVariables.map((item, index) => (
                                <div key={index} className="variable-item">
                                    <div className="variable-info">
                                        <div className="variable-name">
                                            {item.name || item.id}
                                        </div>
                                        <code
                                            className="variable-code"
                                            onClick={() => insertVariable(item.id)}
                                            title={`Click to insert {{${item.id}}}`}
                                        >
                                            {`{{${item.id}}}`}
                                        </code>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => copyToClipboard(`{{${item.id}}}`, e.currentTarget)}
                                        className="copy-btn"
                                        title="Copy to clipboard"
                                    >
                                        📋
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="no-variables">
                                No variables found
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="editor-container">
                {readOnly ? (
                    <div
                        className="preview"
                        style={{ height }}
                        dangerouslySetInnerHTML={{ __html: value || '<p style="color: #999;">No content</p>' }}
                        dir="ltr"
                    />
                ) : (
                    <div
                        ref={editorRef}
                        contentEditable={!readOnly}
                        className="editor-content"
                        style={{ height }}
                        onInput={handleEditorChange}
                        onPaste={handlePaste}
                        placeholder="Type your content here..."
                        suppressContentEditableWarning={true}
                        dir="ltr"
                    />
                )}
            </div>

            <style jsx>{`
                .simple-rich-text-editor {
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background: white;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    direction: ltr;
                }

                .editor-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 12px;
                    background: #f5f5f5;
                    border-bottom: 1px solid #ddd;
                    flex-wrap: wrap;
                    gap: 8px;
                }

                .toolbar-left, .toolbar-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .variables-toggle {
                    background: #11315A;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 13px;
                    font-weight: 500;
                    transition: background 0.2s;
                }

                .variables-toggle:hover {
                    background: #1a4a8a;
                }

                .variables-toggle.active {
                    background: #0f284d;
                }

                .format-buttons {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                    flex-wrap: wrap;
                }

                .format-btn {
                    width: 32px;
                    height: 32px;
                    border: 1px solid #ccc;
                    background: white;
                    border-radius: 3px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #333;
                    transition: all 0.2s;
                    font-size: 14px;
                    padding: 0;
                }

                .format-btn:hover {
                    background: #f0f0f0;
                    border-color: #999;
                }

                .separator {
                    width: 1px;
                    height: 20px;
                    background: #ddd;
                    margin: 0 2px;
                }

                .variables-panel {
                    background: #f9f9f9;
                    border-bottom: 1px solid #ddd;
                    padding: 12px;
                    max-height: 200px;
                    overflow-y: auto;
                }

                .variables-search {
                    margin-bottom: 12px;
                }

                .search-input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    background: white;
                    direction: ltr;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #11315A;
                }

                .variables-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .variable-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px;
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .variable-item:hover {
                    border-color: #11315A;
                }

                .variable-info {
                    flex: 1;
                    min-width: 0;
                }

                .variable-name {
                    font-size: 12px;
                    color: #666;
                    margin-bottom: 4px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .variable-code {
                    font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
                    font-size: 12px;
                    background: #f0f0f0;
                    color: #11315A;
                    padding: 4px 8px;
                    border-radius: 3px;
                    border: 1px solid #ddd;
                    cursor: pointer;
                    display: inline-block;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 100%;
                }

                .variable-code:hover {
                    background: #e0e0e0;
                }

                .copy-btn {
                    background: #11315A;
                    color: white;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 3px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    transition: background 0.2s;
                    margin-left: 8px;
                    flex-shrink: 0;
                }

                .copy-btn:hover {
                    background: #1a4a8a;
                }

                .no-variables {
                    text-align: center;
                    padding: 20px;
                    color: #888;
                    font-size: 14px;
                }

                .editor-container {
                    background: white;
                }

                .editor-content {
                    min-height: 150px;
                    font-size: 14px;
                    color: #333;
                    line-height: 1.5;
                    outline: none;
                    padding: 12px;
                    overflow-y: auto;
                    direction: ltr;
                    text-align: left;
                }

                .editor-content:empty:before {
                    content: attr(placeholder);
                    color: #999;
                    font-style: italic;
                }

                .editor-content:focus {
                    outline: none;
                }

                .preview {
                    overflow-y: auto;
                    background: white;
                    line-height: 1.5;
                    color: #333;
                    padding: 12px;
                    direction: ltr;
                    text-align: left;
                }

                /* Ensure all content is LTR */
                .editor-content * {
                    direction: ltr;
                    text-align: left;
                }

                .preview * {
                    direction: ltr;
                    text-align: left;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .editor-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                    }

                    .toolbar-right {
                        justify-content: center;
                    }

                    .variables-list {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default SimpleRichTextEditor;